import React, { Fragment, useEffect } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import {
  useSingleBillStatementQuery,
  useVANQuery,
} from 'apollo/queries/autogenerate/hooks';
import {
  AutoDebitSettlementStatus,
  BillStatementStatusV2,
} from 'apollo/queries/autogenerate/schemas';
import { COMBINI_FEE } from 'shared/constants/fees';
import isHighPriceBill from 'shared/modules/bill/utils/is-high-price-bill';
import CombiniSettlement from 'shared/modules/settlement/components/organisms/CombiniSettlement';
import SettlementMethods from 'shared/modules/settlement/components/organisms/SettlementMethods';
import {
  ACTION,
  ITEM,
  SCREEN,
} from 'shared/modules/settlement/constants/mixpanel';
import { getNumberOfDaysToNow } from 'shared/utils/date';
import Mixpanel from 'shared/vendor/Mixpanel';
import NavigationBar from 'web/modules/common/components/molecules/NavigationBar';
import routes from 'web/navigation/routes';

const BillSettlementScreen = () => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const isHugeScreen = useMediaQuery({ minWidth: 1024 });
  const navigate = useNavigate();
  const { id, method } = useParams();

  const {
    data: singleBillData,
    error: singleBillError,
  } = useSingleBillStatementQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
    variables: {
      id: id?.trim() || '',
    },
  });

  const billStatement = singleBillData?.billStatement;

  // We only need to check if VAN is available
  const { error: vanError } = useVANQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const chooseMethod = (methodName: string) => {
    if (id?.trim()) {
      const eventName = `${SCREEN.BILL_SETTLEMENT_OPTIONS} - ${ACTION.CLICK}`;

      if (methodName === 'transfer') {
        Mixpanel.trackEvent(eventName, {
          Item: ITEM.BANK_TRANSFER,
        });

        navigate(
          routes.authenticated.home.billSettlementBankTransfer.replace(
            ':id',
            id
          )
        );
      } else {
        Mixpanel.trackEvent(eventName, {
          Item: ITEM.COMBINI,
          'Combini Name':
            methodName === 'loppi' ? 'lawson ministop' : methodName,
        });

        navigate(
          routes.authenticated.home.billSettlementMethod
            .replace(':id', id)
            .replace(':method', methodName)
        );
      }
    }
  };

  // Track how long users view the BillSettlement screen
  useEffect(() => {
    const eventName = `${SCREEN.BILL_SETTLEMENT_OPTIONS} - ${ACTION.PAGE_VIEW}`;

    Mixpanel.timeEvent(eventName);

    return () => Mixpanel.trackEvent(eventName);
  }, []);

  // Track how long users view the Combini instructions
  useEffect(() => {
    const eventName = `${SCREEN.BILL_SETTLEMENT_INSTRUCTIONS} - ${ACTION.PAGE_VIEW}`;

    if (method) {
      Mixpanel.timeEvent(eventName);
    }

    return () => {
      if (method) {
        Mixpanel.trackEvent(eventName, {
          'Screen Type': method === 'loppi' ? 'lawson ministop' : method,
        });
      }
    };
  }, [method]);

  const showSettlementMethods = !method?.trim() || (method && isHugeScreen);

  if (
    billStatement?.statusV2 === BillStatementStatusV2.PAID ||
    (billStatement?.autoDebitSettlement &&
      billStatement?.autoDebitSettlement.pullStatus !==
        AutoDebitSettlementStatus.FAILED)
  ) {
    // If the bill is already paid, then just redirect users to that bill's details
    Mixpanel.trackEvent(
      `${SCREEN.BILL_SETTLEMENT_INSTRUCTIONS} - ${ACTION.SUCCESS}`
    );

    return (
      <Navigate
        to={routes.authenticated.home.billDetails.replace(
          ':id',
          billStatement.id || ''
        )}
        replace
      />
    );
  }

  return (
    <View style={[style.screen, isBigScreen && style.screenBigScreen]}>
      <View
        style={[
          style.container,
          isBigScreen && style.containerBigScreen,
          isHugeScreen && style.containerHugeScreen,
        ]}
      >
        <View
          style={[
            isHugeScreen && style.settlementMethodsHugeScreen,
            !showSettlementMethods && { display: 'none' },
          ]}
        >
          <NavigationBar
            title="ご請求のお支払い方法"
            onBackButtonPress={() => {
              Mixpanel.trackEvent(
                `${SCREEN.BILL_SETTLEMENT_OPTIONS} - ${ACTION.CLICK}`,
                {
                  Item: ITEM.BACK,
                }
              );

              if (billStatement) {
                // Go back to this bill's details
                navigate(
                  routes.authenticated.home.billDetails.replace(
                    ':id',
                    billStatement.id || ''
                  )
                );
              } else {
                navigate(routes.authenticated.home.billDetails);
              }
            }}
            id="navigation-bar"
          />
          {billStatement && (
            <Fragment>
              {/* Overdue bills can only be paid through conbini or bank transfer.
              After 120 days past due date, payment can only be done through bank transfer.  */}
              {getNumberOfDaysToNow(billStatement.dueAt) <= 120 &&
                !isHighPriceBill(billStatement) && (
                  <Text style={[style.text, style.marginBottom]}>
                    {`※コンビニでご請求をお支払いいただく場合、\n1回につき最大税込￥${COMBINI_FEE}の手数料がかかります。`}
                  </Text>
                )}
              <SettlementMethods
                isCombiniAvailable={
                  !isHighPriceBill(billStatement)
                    ? billStatement.combiniSettlementAvailable
                    : Boolean(
                        billStatement.combiniSettlementAvailable &&
                          billStatement.autoDebitSettlement
                      )
                }
                isBankTransferAvailable={
                  billStatement.bankTransferSettlementAvailable
                }
                isUsingFallbackVAN={!!vanError}
                onItemPress={chooseMethod}
              />
            </Fragment>
          )}
          {singleBillError && (
            <Text>
              お支払い情報を表示できませんでした{'\n\n'}
              URLに誤りがあり、支払い情報を表示できませんでした。{'\n'}
              誠に恐れ入りますが、URLに誤りがないかお確かめください。
            </Text>
          )}
        </View>
        {billStatement?.combiniSettlementAvailable && method && (
          <CombiniSettlement
            combini={method as any}
            billId={billStatement.id}
            onError={(paygentError) => {
              // If for some reason generating Paygent does not work
              Mixpanel.trackEvent(
                `${SCREEN.BILL_SETTLEMENT_INSTRUCTIONS} - ${ACTION.ERROR}`,
                {
                  Error: paygentError?.message?.toLowerCase(),
                }
              );
            }}
            showTitle
            style={[
              style.conbiniSettlementDetails,
              isBigScreen && style.conbiniSettlementDetailsBigScreen,
              isHugeScreen && style.conbiniSettlementDetailsHugeScreen,
            ]}
          />
        )}
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  // Template for building a new main screen
  // Scrolls on overflow for both mobile and desktop
  screen: {
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    overflow: 'scroll',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  screenBigScreen: {
    paddingHorizontal: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 960,
  },
  containerHugeScreen: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  settlementMethodsHugeScreen: {
    width: '50%',
    paddingRight: 24,
  },
  conbiniSettlementDetails: {
    marginHorizontal: -16,
    marginTop: -24,
  },
  conbiniSettlementDetailsBigScreen: {
    marginHorizontal: 0,
    marginTop: 0,
  },
  conbiniSettlementDetailsHugeScreen: {
    width: '50%',
    marginTop: 64,
    borderRadius: 8,
    overflow: 'hidden',
  },
  text: {
    fontSize: 12,
    color: '#586065',
    textAlign: 'center',
  },
  marginBottom: {
    marginBottom: 24,
  },
});

export default BillSettlementScreen;
